/**
 * Serial background queue for eval clip encode + upload.
 * Keeps Live Training UI (and the next sequence) moving while a clip is in flight.
 */

const CLAIMS_STORAGE_KEY = "smarttt.evalClip.claimedKeys";

const claimedKeys = new Set();
const inFlightKeys = new Set();
const jobs = [];
let pumping = false;

function readPersistedClaims() {
  if (typeof window === "undefined" || !window.sessionStorage) return [];
  try {
    const raw = window.sessionStorage.getItem(CLAIMS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : [];
  } catch {
    return [];
  }
}

function persistClaims() {
  if (typeof window === "undefined" || !window.sessionStorage) return;
  try {
    window.sessionStorage.setItem(
      CLAIMS_STORAGE_KEY,
      JSON.stringify([...claimedKeys])
    );
  } catch {
    /* quota / private mode */
  }
}

for (const key of readPersistedClaims()) {
  claimedKeys.add(key);
}

export function evalClipKeyClaimed(clipKey) {
  return Boolean(clipKey) && claimedKeys.has(String(clipKey));
}

export function evalClipUploadInFlight(clipKey) {
  return Boolean(clipKey) && inFlightKeys.has(String(clipKey));
}

/** Claim a clip so we never record it again, even if upload is still running. */
export function claimEvalClipKey(clipKey) {
  const key = clipKey != null ? String(clipKey) : "";
  if (!key) return false;
  if (claimedKeys.has(key)) return false;
  claimedKeys.add(key);
  persistClaims();
  return true;
}

/**
 * One encode+upload flight per clip key (survives webpack HMR via sessionStorage).
 * @returns {boolean} false when this clip was already claimed or is uploading.
 */
export function beginEvalClipUpload(clipKey) {
  const key = clipKey != null ? String(clipKey) : "";
  if (!key) return false;
  if (evalClipKeyClaimed(key) || evalClipUploadInFlight(key)) return false;
  claimEvalClipKey(key);
  inFlightKeys.add(key);
  return true;
}

export function finishEvalClipUpload(clipKey) {
  const key = clipKey != null ? String(clipKey) : "";
  if (!key) return;
  inFlightKeys.delete(key);
}

/** Allow a failed upload to be re-recorded and retried. */
export function releaseEvalClipClaim(clipKey) {
  const key = clipKey != null ? String(clipKey) : "";
  if (!key) return;
  claimedKeys.delete(key);
  inFlightKeys.delete(key);
  persistClaims();
}

export function enqueueEvalClipJob(job) {
  if (typeof job !== "function") return;
  jobs.push(job);
  pumpEvalClipJobs();
}

async function pumpEvalClipJobs() {
  if (pumping) return;
  pumping = true;
  while (jobs.length) {
    const job = jobs.shift();
    try {
      await job();
    } catch (err) {
      console.warn("Eval clip background job failed:", err?.message || err);
    }
    await new Promise((resolve) => window.setTimeout(resolve, 0));
  }
  pumping = false;
}
